import React, { useState } from 'react';
import { MapPinIcon, PlusIcon, PencilIcon, TrashIcon, SearchIcon, XIcon, ExternalLinkIcon } from 'lucide-react';
import { Card, StepHeading, BlockLabel, Field, Toggle, Helper } from '../SettingsUI';
import { ShopConfig, ShopLocation, COUNTRY_OPTIONS } from '../../../data/shopConfig';
interface Props {
  config: ShopConfig;
  update: (patch: Partial<ShopConfig>) => void;
}
export function LocationsContact({
  config,
  update
}: Props) {
  const [pickingCountry, setPickingCountry] = useState(false);
  const [countryQuery, setCountryQuery] = useState('');
  const [editing, setEditing] = useState<ShopLocation | null>(null);
  const available = COUNTRY_OPTIONS.filter(c => !config.countries.includes(c) && c.toLowerCase().includes(countryQuery.toLowerCase()));
  const saveLocation = (loc: ShopLocation) => {
    const exists = config.locations.some(l => l.id === loc.id);
    update({
      locations: exists ? config.locations.map(l => l.id === loc.id ? loc : l) : [...config.locations, loc]
    });
    setEditing(null);
  };
  const setContact = (patch: Partial<ShopConfig['contact']>) => update({
    contact: {
      ...config.contact,
      ...patch
    }
  });
  return <div className="space-y-5" data-id="element-3945" data-mp="16dqealrf4qxj">
      <StepHeading title="Where can customers find or contact you?" desc="Add the countries you serve, your branches, and how customers should reach you." data-id="element-3946" data-mp="16cp1ocur7wdp" />

      {/* Countries */}
      <Card data-id="element-3947" data-mp="1g2i2citcyr36">
        <BlockLabel data-id="element-3948" data-mp="1g31jhxb6pbg5">Operating Countries</BlockLabel>
        <div className="flex flex-wrap items-center gap-2" data-id="element-3949" data-mp="1u32i163z86sg">
          {config.countries.map(c => <span key={c} className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1.5 rounded-full bg-slate-50 border border-slate-200 text-sm font-medium text-slate-700" data-id="element-3950" data-mp="1p4hkxfk0u388">
              {c}
              <button onClick={() => update({
            countries: config.countries.filter(x => x !== c)
          })} aria-label={`Remove ${c}`} className="p-0.5 rounded-full text-slate-400 hover:text-error hover:bg-white transition-colors duration-150 ease-out" data-id="element-3951" data-mp="3qww7wt1f9xvi">
                <XIcon className="w-3.5 h-3.5" data-id="element-3952" data-mp="28taehddetck2" />
              </button>
            </span>)}
          <button onClick={() => setPickingCountry(p => !p)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-dashed border-slate-300 text-sm font-medium text-slate-500 hover:border-primary-indigo/50 hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-3953" data-mp="2dqamhtttkpdt">
            <PlusIcon className="w-3.5 h-3.5" data-id="element-3954" data-mp="2sib12uwtda89" />
            Add country
          </button>
        </div>

        {pickingCountry && <div className="mt-4 p-3 rounded-xl border border-slate-200 bg-slate-50/60" data-id="element-3955" data-mp="31gdanyum5b75">
            <div className="relative mb-2" data-id="element-3956" data-mp="ptjg253mydie">
              <SearchIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-3957" data-mp="pucgkoc046s3" />
              <input autoFocus value={countryQuery} onChange={e => setCountryQuery(e.target.value)} placeholder="Search countries…" className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-primary-indigo/50" data-id="element-3958" data-mp="kswvwxdqk2a8" />
            </div>
            <div className="max-h-40 overflow-y-auto" data-id="element-3959" data-mp="14i7ecj4v91c1">
              {available.map(c => <button key={c} onClick={() => {
            update({
              countries: [...config.countries, c]
            });
            setCountryQuery('');
            setPickingCountry(false);
          }} className="w-full text-left px-3 py-2 rounded-lg text-sm text-slate-700 hover:bg-white transition-colors duration-150 ease-out" data-id="element-3960" data-mp="zqxmdbvuhlwz">
                  {c}
                </button>)}
              {available.length === 0 && <p className="px-3 py-2 text-sm text-slate-400" data-id="element-3961" data-mp="y1dwysqdhg3d">
                  No more countries to add.
                </p>}
            </div>
          </div>}
      </Card>

      {/* Locations */}
      <Card data-id="element-3962" data-mp="1qpjhbedil32o">
        <BlockLabel hint="Customers see these on your shop" data-id="element-3963" data-mp="1qquozpa2yuz2">Locations</BlockLabel>
        <div className="space-y-2.5" data-id="element-3964" data-mp="1urmwacze6psp">
          {config.locations.map(l => <div key={l.id} className="flex items-center gap-3 p-4 rounded-xl border border-slate-200" data-id="element-3965" data-mp="1h08axzi0icih">
              <span className="w-9 h-9 rounded-xl bg-primary-indigo/[0.06] flex items-center justify-center flex-shrink-0" data-id="element-3966" data-mp="1lxia46v7e2ji">
                <MapPinIcon className="w-4 h-4 text-primary-indigo" data-id="element-3967" data-mp="29mkqlbe3ppcf" />
              </span>
              <div className="flex-1 min-w-0" data-id="element-3968" data-mp="2egp0o3inydhq">
                <p className="text-sm font-semibold text-dark-navy truncate" data-id="element-3969" data-mp="2ntnxw4kcapcw">
                  {l.name}
                </p>
                <p className="text-xs text-slate-500 truncate" data-id="element-3970" data-mp="2jrszp8xpgs3c">{l.address}</p>
              </div>
              <button onClick={() => setEditing(l)} className="p-2 rounded-lg text-slate-400 hover:text-primary-indigo hover:bg-slate-50 transition-colors duration-150 ease-out" aria-label={`Edit ${l.name}`} data-id="element-3971" data-mp="2xio1k19se8xf">
                <PencilIcon className="w-4 h-4" data-id="element-3972" data-mp="20u89nyj7fnh5" />
              </button>
              <button onClick={() => update({
            locations: config.locations.filter(x => x.id !== l.id)
          })} className="p-2 rounded-lg text-slate-400 hover:text-error hover:bg-error/5 transition-colors duration-150 ease-out" aria-label={`Remove ${l.name}`} data-id="element-3973" data-mp="20w2u7bqkddkp">
                <TrashIcon className="w-4 h-4" data-id="element-3974" data-mp="3lbxt3kjvb4ik" />
              </button>
            </div>)}
          <button onClick={() => setEditing({
          id: `l${Date.now()}`,
          name: '',
          address: '',
          mapPin: ''
        })} className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-dashed border-slate-300 text-sm font-semibold text-slate-500 hover:border-primary-indigo/50 hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-3975" data-mp="3tclxfqnvqgg9">
            <PlusIcon className="w-4 h-4" data-id="element-3976" data-mp="3ej0mqvixa4lu" />
            Add another location
          </button>
        </div>

        {editing && <div className="mt-4 p-4 rounded-xl border border-primary-indigo/20 bg-primary-indigo/[0.03] space-y-4" data-id="element-3977" data-mp="3obojxrdteal3">
            <Field label="Location name" value={editing.name} onChange={name => setEditing({
          ...editing,
          name
        })} placeholder="e.g. Dubai Branch" data-id="element-3978" data-mp="2qur6e94ha923" />
            <Field label="Address" value={editing.address} onChange={address => setEditing({
          ...editing,
          address
        })} placeholder="e.g. Al Quoz, Dubai" data-id="element-3979" data-mp="2jdb6e2xyz8oq" />
            <div data-id="element-3980" data-mp="25da54mdslz8q">
              <BlockLabel data-id="element-3981" data-mp="25dta72icgt5c">Find it on Google Maps</BlockLabel>
              <div className="flex gap-2" data-id="element-3982" data-mp="20li9vyjoz5c9">
                <input value={editing.mapPin} onChange={e => setEditing({
              ...editing,
              mapPin: e.target.value
            })} placeholder="Search a place, or paste a Maps link" className="flex-1 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm outline-none focus:border-primary-indigo/50" data-id="element-3983" data-mp="20kgw3mbak4qu" />
                <a href="https://maps.google.com" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-600 hover:border-primary-indigo/50 transition-colors duration-150 ease-out" data-id="element-3984" data-mp="35k56hipg9xyh">
                  <ExternalLinkIcon className="w-4 h-4" data-id="element-3985" data-mp="2mscdvpeamera" />
                  Open Maps
                </a>
              </div>
              {editing.mapPin && <div className="mt-3 rounded-xl overflow-hidden border border-slate-200 h-40" data-id="element-3986" data-mp="2cv6ocllbuvt6">
                  <iframe title="Map preview" src={`https://maps.google.com/maps?q=${encodeURIComponent(editing.mapPin)}&z=13&output=embed`} className="w-full h-full" data-id="element-3987" data-mp="2htrnsisr9bgk" />
                </div>}
            </div>
            <div className="flex items-center gap-2" data-id="element-3988" data-mp="1p79rc3wdvltr">
              <button onClick={() => saveLocation(editing)} disabled={!editing.name.trim()} className="px-4 py-2.5 bg-primary-indigo text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 disabled:opacity-40 transition-colors duration-150 ease-out" data-id="element-3989" data-mp="1p82yv7362fcy">
                Save location
              </button>
              <button onClick={() => setEditing(null)} className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-600 hover:border-slate-300 transition-colors duration-150 ease-out" data-id="element-3990" data-mp="285dpk3aqg0gs">
                Cancel
              </button>
            </div>
          </div>}
      </Card>

      {/* Contact */}
      <Card className="space-y-4" data-id="element-3991" data-mp="2qw3pbj50b75f">
        <BlockLabel data-id="element-3992" data-mp="2qx6gzta5ldme">Contact methods</BlockLabel>
        <Field label="Email" value={config.contact.email} onChange={email => setContact({
        email
      })} type="email" data-id="element-3993" data-mp="2lyc04wvqxz4x" />
        <Toggle label="Show email on shop" checked={config.contact.showEmail} onChange={showEmail => setContact({
        showEmail
      })} data-id="element-3994" data-mp="23oib4i0kth0r" />
        <div className="h-px bg-slate-100" data-id="element-3995" data-mp="1uuvhaxfxinu7" />
        <Field label="Phone" value={config.contact.phone} onChange={phone => setContact({
        phone
      })} data-id="element-3996" data-mp="1pv91qhcmovi8" />
        <Toggle label="Show phone on shop" checked={config.contact.showPhone} onChange={showPhone => setContact({
        showPhone
      })} data-id="element-3997" data-mp="2mk8eo9o5qlgr" />
        <div className="h-px bg-slate-100" data-id="element-3998" data-mp="2dkbvgnkjeet3" />
        <Field label="WhatsApp" value={config.contact.whatsapp} onChange={whatsapp => setContact({
        whatsapp
      })} data-id="element-3999" data-mp="2djab1shmsfyp" />
        <Toggle label="Show WhatsApp on shop" checked={config.contact.showWhatsapp} onChange={showWhatsapp => setContact({
        showWhatsapp
      })} data-id="element-4000" data-mp="1267rqpo5dcau" />
        <Helper data-id="element-4001" data-mp="n2okvfrivpi2">
          Anything switched off stays private — your AI assistant can still
          pass enquiries to you.
        </Helper>
      </Card>
    </div>;
}